import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, Wrench, Car, FileText,
  Package, CarFront, Users, Settings, ClipboardList, Truck, PhoneCall, KanbanSquare, CalendarRange, ShieldCheck, Gauge, Flame, ClipboardCheck, BarChart3, Megaphone,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, end: true, module: "dashboard" },
  { to: "/planning", label: "Planning atelier", icon: CalendarDays, module: "planning" },
  { to: "/ordres", label: "Ordres de réparation", icon: Wrench, badge: "or_ouverts", module: "ordres" },
  { to: "/ventes", label: "Ventes véhicules", icon: Car, badge: "dossiers_en_cours", module: "ventes" },
  { to: "/dossiers-administratifs", label: "Dossiers admin.", icon: ClipboardCheck, module: "ventes", rolesOnly: ["secretaire_commerciale", "admin"] },
  { to: "/pipeline", label: "Pipeline commerce", icon: KanbanSquare, badge: "opportunites_actives", module: "pipeline" },
  { to: "/plans-action-reseau", label: "Plans d'action réseau", icon: Megaphone, module: "pipeline", rolesOnly: ["vendeur", "chef_ventes", "directeur", "dg", "admin"] },
  { to: "/agenda", label: "Agenda", icon: CalendarRange, module: "agenda" },
  { to: "/facturation", label: "Facturation", icon: FileText, badge: "factures_brouillons", module: "facturation" },
  { to: "/stock-pieces", label: "Stock pièces", icon: Package, module: "stock_pieces" },
  { to: "/stock-vehicules", label: "Stock véhicules", icon: CarFront, module: "stock_vehicules" },
  { to: "/pilote-stock", label: "Pilote Stock VO", icon: BarChart3, module: "stock_vehicules", park: true },
  { to: "/clients", label: "Clients", icon: Users, module: "clients" },
  { to: "/portefeuille", label: "Portefeuille", icon: Flame, module: "clients" },
  { to: "/parametres", label: "Paramètres", icon: Settings, module: "parametres" },
];

export default function Sidebar({ stats, ouvert, onClose }) {
  const { auth, peutVoir } = useAuth();
  const items = NAV.filter((n) => {
    if (n.rolesOnly && !n.rolesOnly.includes(auth?.user?.role)) return false;
    return n.module === "dashboard" || peutVoir(n.module);
  });
  const isAdmin = auth?.user?.role === "admin";
  const estManager = ["chef_ventes", "directeur", "dg", "admin"].includes(auth?.user?.role);
  if (estManager) {
    items.splice(1, 0, { to: "/vue-globale", label: "Vue globale", icon: Gauge });
  }
  return (
    <aside className={`idc-sidebar fixed inset-y-0 left-0 flex flex-col z-40 transform transition-transform duration-200 lg:translate-x-0 ${ouvert ? "translate-x-0" : "-translate-x-full"}`} data-testid="sidebar">
      <div className="flex items-center gap-3 px-5 pt-5 pb-6">
        <div
          className="w-9 h-9 rounded-[10px] flex items-center justify-center font-extrabold text-white text-sm shrink-0"
          style={{ background: "linear-gradient(105deg, #14213D 52%, #E5232E 52%)" }}
        >
          ID
        </div>
        <div>
          <div className="text-white font-bold text-[17px] leading-tight">ID Core</div>
          <div className="text-[10px] tracking-[0.25em] text-[#8FA3C0] font-semibold">
            DMS<span className="inline-block w-7 border-b-2 border-[#E5232E] ml-1 align-middle" />
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto pb-4">
        {items.map(({ to, label, icon: Icon, badge, end, park }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onClose}
            className={({ isActive }) => `idc-nav-item ${park ? "park" : ""} ${isActive ? "active" : ""}`}
            data-testid={`nav-${to === "/" ? "dashboard" : to.slice(1)}`}
          >
            <Icon className="w-[17px] h-[17px] shrink-0" />
            <span className="flex-1">{label}</span>
            {badge && stats?.[badge] > 0 && (
              <span className="bg-[#2F6FED] text-white text-[10.5px] font-bold rounded-full min-w-[19px] h-[19px] flex items-center justify-center px-1">
                {stats[badge]}
              </span>
            )}
          </NavLink>
        ))}
        {isAdmin && (
          <NavLink to="/administration" onClick={onClose} className={({ isActive }) => `idc-nav-item ${isActive ? "active" : ""}`} data-testid="nav-administration">
            <ShieldCheck className="w-[17px] h-[17px] shrink-0" />
            <span className="flex-1">Administration</span>
          </NavLink>
        )}
      </nav>

      <div className="mx-4 mb-4 rounded-xl p-4" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid="widget-activite">
        <div className="text-white text-[13px] font-semibold mb-3">Activité du jour</div>
        {[
          { icon: ClipboardList, val: stats?.or_ouverts, label: "OR ouverts" },
          { icon: Truck, val: stats?.livraisons_prevues, label: "livraisons prévues" },
          { icon: PhoneCall, val: stats?.rdv_jour, label: "RDV aujourd'hui" },
        ].map(({ icon: Icon, val, label }, i) => (
          <div key={i} className="flex items-center gap-3 py-1.5">
            <Icon className="w-4 h-4 text-[#8FA3C0]" />
            <div>
              <span className="text-white font-bold text-[15px]">{val ?? "—"}</span>
              <span className="text-[#8FA3C0] text-xs ml-2">{label}</span>
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
}
